import { useState, type FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { fmt } from '@/lib/format';

type Articulo = { id: string; clave: string; descripcion: string; unidad_clave?: string; partida_cog_id?: string; costo_promedio: number };
type UA = { id: string; clave: string; nombre: string };
type PresupEg = { id: string; partida_codigo: string; partida_nombre: string; disponible: number };

type Linea = {
  articulo_id: string; articulo_label: string;
  descripcion: string; cantidad: number;
  precio_unitario_estimado: number;
  partida_cog_id: string;
  es_activo_fijo: boolean;
};

export function NuevaRequisicionModal({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated: () => void }) {
  const [v, setV] = useState({
    unidad_administrativa_id: '',
    solicitante_nombre: '',
    justificacion: '',
    tipo_adjudicacion: 'directa',
    fundamento_legal: '',
    presupuesto_egreso_id: '',
    partida_cog_id: '',
    iva_estimado: 0,
    observaciones: '',
  });
  const [lineas, setLineas] = useState<Linea[]>([]);
  const [articuloPick, setArticuloPick] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [subm, setSubm] = useState(false);

  const { data: uas } = useQuery({ queryKey: ['uas'], queryFn: () => api<UA[]>('/unidades-administrativas'), enabled: open });
  const { data: articulos } = useQuery({ queryKey: ['articulos-pick'], queryFn: () => api<{items:Articulo[]}>('/articulos?limit=500'), enabled: open });
  const { data: presupuestos } = useQuery({ queryKey: ['presupuestos-egresos'], queryFn: () => api<PresupEg[]>('/presupuesto/egresos'), enabled: open });

  function agregarLinea() {
    if (!articuloPick) return;
    const art = articulos?.items.find(a => a.id === articuloPick);
    if (!art) return;
    setLineas([...lineas, {
      articulo_id: art.id,
      articulo_label: `${art.clave} — ${art.descripcion}`,
      descripcion: art.descripcion,
      cantidad: 1,
      precio_unitario_estimado: art.costo_promedio || 0,
      partida_cog_id: art.partida_cog_id ?? v.partida_cog_id,
      es_activo_fijo: false,
    }]);
    setArticuloPick('');
  }

  function agregarLibre() {
    setLineas([...lineas, {
      articulo_id: '', articulo_label: '(texto libre)',
      descripcion: '', cantidad: 1, precio_unitario_estimado: 0,
      partida_cog_id: v.partida_cog_id, es_activo_fijo: false,
    }]);
  }

  const subtotal = lineas.reduce((a, l) => a + l.cantidad * l.precio_unitario_estimado, 0);
  const total = subtotal + v.iva_estimado;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (lineas.length === 0) { setErr('Agrega al menos una línea'); return; }
    if (lineas.some(l => !l.descripcion)) { setErr('Falta descripción en alguna línea'); return; }
    setSubm(true); setErr(null);
    try {
      await api('/requisiciones', {
        method: 'POST',
        body: {
          ...v,
          iva_estimado: Number(v.iva_estimado),
          lineas: lineas.map(l => ({
            articulo_id: l.articulo_id || undefined,
            descripcion: l.descripcion,
            cantidad: l.cantidad,
            precio_unitario_estimado: l.precio_unitario_estimado,
            partida_cog_id: l.partida_cog_id || v.partida_cog_id || undefined,
            es_activo_fijo: l.es_activo_fijo,
          })),
        },
      });
      onCreated();
    } catch (x) {
      setErr(x instanceof ApiError ? x.message : 'Error');
    } finally { setSubm(false); }
  }

  return (
    <Modal open={open} onClose={onClose} title="Nueva requisición de compra"
      subtitle="Al autorizar se comprometerá presupuesto contra la partida seleccionada"
      size="xl"
      footer={<>
        <Button variant="ghost" type="button" onClick={onClose}>Cancelar</Button>
        <Button type="submit" form="req-form" loading={subm}>Crear · {fmt.money(total)}</Button>
      </>}>
      <form id="req-form" onSubmit={submit} className="flex flex-col gap-4">
        {err && <div className="rounded-md bg-rose-50 px-3 py-2 text-[13px] text-rose-700">{err}</div>}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="text-[13px] font-medium text-ink">Unidad administrativa</label>
            <select value={v.unidad_administrativa_id} onChange={(e) => setV({ ...v, unidad_administrativa_id: e.target.value })}
              className="mt-1.5 h-10 w-full rounded-md border border-border bg-surface px-3 text-[14px] shadow-subtle">
              <option value="">(sin asignar)</option>
              {(uas ?? []).map(u => <option key={u.id} value={u.id}>{u.clave} — {u.nombre}</option>)}
            </select>
          </div>
          <Input name="solicitante_nombre" label="Nombre del solicitante"
            value={v.solicitante_nombre} onChange={(e) => setV({ ...v, solicitante_nombre: e.target.value })} />
          <div>
            <label className="text-[13px] font-medium text-ink">Tipo de adjudicación</label>
            <select value={v.tipo_adjudicacion} onChange={(e) => setV({ ...v, tipo_adjudicacion: e.target.value })}
              className="mt-1.5 h-10 w-full rounded-md border border-border bg-surface px-3 text-[14px] shadow-subtle">
              <option value="directa">Adjudicación directa</option>
              <option value="invitacion_3">Invitación a 3 personas</option>
              <option value="licitacion_publica">Licitación pública</option>
              <option value="otra">Otra</option>
            </select>
          </div>
          <Input name="fundamento_legal" label="Fundamento legal" hint="Ej: Art. 42 LAASSP"
            value={v.fundamento_legal} onChange={(e) => setV({ ...v, fundamento_legal: e.target.value })} />
          <div className="sm:col-span-2">
            <label className="text-[13px] font-medium text-ink">Presupuesto / partida a comprometer</label>
            <select value={v.presupuesto_egreso_id} onChange={(e) => {
                const p = presupuestos?.find(pe => pe.id === e.target.value);
                setV({ ...v, presupuesto_egreso_id: e.target.value, partida_cog_id: '' });
                void p;
              }}
              className="mt-1.5 h-10 w-full rounded-md border border-border bg-surface px-3 text-[14px] shadow-subtle">
              <option value="">(sin presupuesto)</option>
              {(presupuestos ?? []).map(p => (
                <option key={p.id} value={p.id}>{p.partida_codigo} — {p.partida_nombre} · disponible {fmt.money(p.disponible)}</option>
              ))}
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className="text-[13px] font-medium text-ink">Justificación</label>
            <textarea required rows={2} value={v.justificacion}
              onChange={(e) => setV({ ...v, justificacion: e.target.value })}
              className="mt-1.5 w-full rounded-md border border-border bg-surface px-3 py-2 text-[14px] shadow-subtle" />
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between">
            <h4 className="text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-mute">Líneas</h4>
            <div className="flex items-center gap-2">
              <select value={articuloPick} onChange={(e) => setArticuloPick(e.target.value)}
                className="h-9 rounded-md border border-border bg-surface px-2 text-[13px]">
                <option value="">(artículo)</option>
                {(articulos?.items ?? []).map(a => (
                  <option key={a.id} value={a.id}>{a.clave} — {a.descripcion.slice(0, 40)}</option>
                ))}
              </select>
              <Button size="sm" variant="secondary" type="button" onClick={agregarLinea}><Plus size={14} /> Añadir</Button>
              <Button size="sm" variant="ghost" type="button" onClick={agregarLibre}>+ Texto libre</Button>
            </div>
          </div>

          <div className="mt-2 overflow-hidden rounded-md border border-border">
            {lineas.length === 0 ? (
              <div className="p-6 text-center text-[12px] text-ink-mute">Sin líneas.</div>
            ) : (
              <table className="min-w-full text-[13px]">
                <thead className="bg-muted/60 text-left text-[10px] font-semibold uppercase tracking-wide text-ink-mute">
                  <tr>
                    <th className="px-3 py-2">Descripción</th>
                    <th className="px-3 py-2 text-right">Cantidad</th>
                    <th className="px-3 py-2 text-right">P.U. estimado</th>
                    <th className="px-3 py-2 text-right">Total</th>
                    <th className="px-3 py-2 text-center">Activo fijo</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {lineas.map((l, i) => (
                    <tr key={i}>
                      <td className="px-3 py-2">
                        {l.articulo_id ? (
                          <div>
                            <div className="text-[11px] text-ink-mute">{l.articulo_label}</div>
                            <input type="text" value={l.descripcion}
                              onChange={(e) => setLineas(lineas.map((x, j) => j === i ? { ...x, descripcion: e.target.value } : x))}
                              className="w-full text-[12px] bg-transparent" />
                          </div>
                        ) : (
                          <input type="text" placeholder="Descripción libre"
                            value={l.descripcion}
                            onChange={(e) => setLineas(lineas.map((x, j) => j === i ? { ...x, descripcion: e.target.value } : x))}
                            className="w-full h-8 rounded border border-border bg-surface px-2" />
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <input type="number" step="0.001" value={l.cantidad}
                          onChange={(e) => setLineas(lineas.map((x, j) => j === i ? { ...x, cantidad: parseFloat(e.target.value) || 0 } : x))}
                          className="num h-8 w-20 rounded border border-border bg-surface px-2 text-right" />
                      </td>
                      <td className="px-3 py-2">
                        <input type="number" step="0.0001" value={l.precio_unitario_estimado}
                          onChange={(e) => setLineas(lineas.map((x, j) => j === i ? { ...x, precio_unitario_estimado: parseFloat(e.target.value) || 0 } : x))}
                          className="num h-8 w-28 rounded border border-border bg-surface px-2 text-right" />
                      </td>
                      <td className="num px-3 py-2 text-right font-semibold">{fmt.money(l.cantidad * l.precio_unitario_estimado)}</td>
                      <td className="px-3 py-2 text-center">
                        <input type="checkbox" checked={l.es_activo_fijo}
                          onChange={(e) => setLineas(lineas.map((x, j) => j === i ? { ...x, es_activo_fijo: e.target.checked } : x))}
                          className="h-3.5 w-3.5 accent-brand-500" />
                      </td>
                      <td className="px-3 py-2 text-right">
                        <button type="button" onClick={() => setLineas(lineas.filter((_, j) => j !== i))}
                          className="rounded p-1 text-ink-mute hover:bg-muted hover:text-danger"><Trash2 size={14} /></button>
                      </td>
                    </tr>
                  ))}
                  <tr className="bg-muted/40">
                    <td colSpan={3} className="px-3 py-2 text-right text-[11px] font-semibold uppercase text-ink-mute">Subtotal</td>
                    <td className="num px-3 py-2 text-right font-semibold">{fmt.money(subtotal)}</td>
                    <td colSpan={2}></td>
                  </tr>
                </tbody>
              </table>
            )}
          </div>
        </div>

        <Input name="iva" label="IVA estimado" type="number" step="0.01"
          value={v.iva_estimado} onChange={(e) => setV({ ...v, iva_estimado: parseFloat(e.target.value) || 0 })} />
      </form>
    </Modal>
  );
}
